import { FC, ReactNode, useMemo } from "react";
import { WebClipV1SessionPart, SessionEventKind } from "../session.types";
import styled from "@emotion/styled";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import { SessionPartContext } from "@/mds/components/sessions/session-parts/session-part.types";
import { MDSBubble } from "@/mds/components/bubble/bubble";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble/bubble.types";
import { sessionPartContainsImage } from "@/mds/components/sessions/session.utils";
import {
  WEB_CLIP_MAX_HEIGHT_SIZE_PX,
  WEB_CLIP_MAX_WIDTH_SIZE_PX,
} from "@/mds/components/sessions/session.constants";
import { isNumber } from "lodash-es";
import { motion, AnimatePresence } from "motion/react";

interface Props {
  part: WebClipV1SessionPart;
  context: SessionPartContext;
}

export const MDSSessionWebClipV1Part: FC<Props> = ({ part, context }) => {
  const partContainsImage = sessionPartContainsImage(part);

  const WrapperComponent: FC<IWrapperComponentProps> = (() => {
    if (context.eventKind === SessionEventKind.AgentActionV1) {
      return AgentActionWrapper;
    }

    if (partContainsImage) {
      return ImageWrapper;
    }

    return StandardWrapper;
  })();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const meta: Record<string, unknown> | undefined = part._meta;

  const completionPercentage = useMemo(() => {
    const percentage = meta?.completion_percentage as number | undefined;

    if (isNumber(percentage)) {
      return percentage;
    }

    return 1.0;
  }, [meta?.completion_percentage]);

  const isPendingCompletion = useMemo(() => {
    return completionPercentage < 1.0;
  }, [completionPercentage]);

  return (
    <WrapperComponent isLastInGroup={context.isLastInGroup}>
      <Container eventKind={context.eventKind}>
        {part.clipped_image_uploaded_file_url ? (
          <WebClipImage
            src={part.clipped_image_uploaded_file_url}
            alt={part.captured_website_title ?? "Clipped Web Page"}
            hasClippedTextContent={!!part.clipped_text_content}
          />
        ) : null}
        {part.clipped_text_content ? (
          <ClippedTextContent>{part.clipped_text_content}</ClippedTextContent>
        ) : null}
        <AnimatePresence>
          {isPendingCompletion && (
            <LoadingOverlay exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <LoadingContent>
                <SmallSpinner
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1,
                    ease: "linear",
                    repeat: Infinity,
                  }}
                />
              </LoadingContent>
            </LoadingOverlay>
          )}
        </AnimatePresence>
      </Container>
    </WrapperComponent>
  );
};

interface IWrapperComponentProps {
  children: ReactNode;
  isLastInGroup?: boolean;
}

const AgentActionWrapper: FC<IWrapperComponentProps> = ({ children }) => {
  return <>{children}</>;
};

const ImageWrapper: FC<IWrapperComponentProps> = ({ children, isLastInGroup }) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Image}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {children}
    </Bubble>
  );
};

const StandardWrapper: FC<IWrapperComponentProps> = ({ children, isLastInGroup }) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Grey}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {children}
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.xxs};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
  margin-right: 0;
`;

const Container = styled(MDSSessionPartContainer)<{
  eventKind: SessionEventKind;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: ${WEB_CLIP_MAX_WIDTH_SIZE_PX}px;
  max-height: ${WEB_CLIP_MAX_HEIGHT_SIZE_PX}px;
  align-items: center;
  margin-top: ${({ theme, eventKind }) =>
    eventKind === SessionEventKind.AgentActionV1 ? theme.spacing.xs : "0"};
  margin-bottom: ${({ theme, eventKind }) =>
    eventKind === SessionEventKind.AgentActionV1 ? theme.spacing.xs : "0"};
  position: relative;
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const WebClipImage = styled.img<{ hasClippedTextContent: boolean }>`
  aspect-ratio: 3 / 2;
  object-fit: cover;
  object-position: top;
  border-radius: ${({ hasClippedTextContent }) =>
    hasClippedTextContent ? "14px 14px 0 0" : "14px"};
  border-bottom: none;
  height: 100%;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.grey.x100};
`;

const ClippedTextContent = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey.x600};
  padding: 0px 12px 16px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  width: 100%;
`;

const SmallSpinner = styled(motion.div)`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-radius: 50%;
  border-top-color: ${({ theme }) => theme.colors.grey.x600};
  mask-image: linear-gradient(
    to left,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0.6) 65%,
    rgba(0, 0, 0, 0) 100%
  );
  will-change: transform;
`;
